import {
  Alert,
  Box,
  Button,
  ButtonProps,
  Icon,
  Typography
} from "@outschool/backpack";
import { OSSubscriptions } from "@outschool/business-rules";
import { CurrencyCode } from "@outschool/gql-backend-generated";
import {
  EnrolledClassDetailsQueryVariables,
  WithdrawAndRefundEnrollmentMutation,
  WithdrawAndRefundEnrollmentMutationVariables,
  WithdrawAndRefundGiftEnrollmentMutation,
  WithdrawAndRefundGiftEnrollmentMutationVariables
} from "@outschool/gql-frontend-generated";
import { faInfoCircle } from "@outschool/icons";
import {
  Trans,
  renderCurrencyStringWithCurrencyConversion,
  useLocale,
  useTranslation
} from "@outschool/localization";
import { SupportRoutes, addParamsToUrl, enrolledPath } from "@outschool/routes";
import { useOrderRefunded } from "@outschool/ui-analytics";
import { DocumentNode, useMutation } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { ExternalLink, TrackedButton } from "@outschool/ui-components-shared";
import { useTopNoticeContext } from "@outschool/ui-components-website";
import {
  ModalActionButtons,
  Select
} from "@outschool/ui-legacy-component-library";
import _ from "lodash";
import React, { FormEvent, useRef, useState } from "react";

import * as Enrollment from "../../../shared/Enrollment";
import * as EnrollmentPaymentRefund from "../../../shared/EnrollmentPaymentRefund";
import * as Env from "../../../shared/Env";
import useCurrencyEnrollmentEnabled from "../../../shared/hooks/useCurrencyEnrollmentEnabled";
import {
  EnrollmentListQuery,
  getEnrollmentListQueryVariables
} from "../../queries/EnrollmentListQuery";
import {
  withdrawAndRefundEnrollmentMutation,
  withdrawAndRefundGiftEnrollmentMutation
} from "../../queries/EnrollmentMutations";
import ActivitySummary from "../enrolling/ActivitySummary";
import ErrorMessage from "../ErrorMessage";
import Link from "../Link";
import PageModal from "../PageModal";
import {
  EnrolledClassActivity,
  EnrolledClassEnrollment,
  EnrolledClassGiftEnrollment,
  EnrolledClassSection
} from "./EnrolledClassTypes";
import StopEnrollmentPaymentSchedule from "./StopEnrollmentPaymentSchedule";
import StopSubscription from "./StopSubscription";
import { useParentRefundReasonLabels } from "./useParentRefundReasonLabels";

type ParentWithdrawButtonProps = ButtonProps & {
  section: EnrolledClassSection;
  activity: EnrolledClassActivity;
  enrollment: EnrolledClassEnrollment | EnrolledClassGiftEnrollment;
  rootQuery?: {
    query: DocumentNode;
    variables: EnrolledClassDetailsQueryVariables;
  };
  open: boolean;
  setOpen: (a: boolean) => void;
  redirectToPause: () => void;
};

export default React.forwardRef(ParentWithdrawButton);

function ParentWithdrawButton(
  {
    enrollment,
    activity,
    section,
    rootQuery,
    open,
    setOpen,
    redirectToPause,
    ...buttonProps
  }: ParentWithdrawButtonProps,
  ref: React.Ref<any>
) {
  const { t } = useTranslation(
    "client\\components\\account\\ParentWithdrawButton"
  );

  const studentName = Enrollment.displayName(enrollment);
  if (enrollment.canBeWithdrawn) {
    return (
      <Withdraw
        ref={ref}
        activity={activity}
        section={section}
        studentName={studentName}
        enrollment={enrollment}
        open={open}
        setOpen={setOpen}
        redirectToPause={redirectToPause}
        {...buttonProps}
      />
    );
  } else if (enrollment.canBeStopped) {
    if (activity.isClub) {
      return (
        <StopEnrollmentPaymentSchedule
          ref={ref}
          studentName={studentName}
          enrollment={enrollment}
          activity={activity}
          rootQuery={rootQuery}
          {...buttonProps}
        />
      );
    } else {
      return (
        <StopSubscription
          ref={ref}
          buttonLabel={t`Stop subscription`}
          activity={activity}
          section={section}
          studentName={studentName}
          enrollment={enrollment}
          {...buttonProps}
        />
      );
    }
  }

  return null;
}

type WithdrawProps = ButtonProps & {
  section: EnrolledClassSection;
  activity: EnrolledClassActivity;
  enrollment: EnrolledClassEnrollment | EnrolledClassGiftEnrollment;
  studentName: string;
  open: boolean;
  setOpen: (a: boolean) => void;
  redirectToPause: () => void;
};

type UpcomingEnrollmentPayment = EnrolledClassEnrollment["payments"][0] & {
  credit_account_type?: string;
};

const Withdraw = React.forwardRef(_Withdraw);

function _Withdraw(
  {
    activity,
    section,
    enrollment,
    studentName,
    open,
    setOpen,
    onClick,
    redirectToPause,
    ...buttonProps
  }: WithdrawProps,
  ref: React.Ref<any>
) {
  const { t } = useTranslation(
    "client\\components\\account\\ParentWithdrawButton"
  );

  const { setTopNotice } = useTopNoticeContext();
  const { currentUser } = useSession();
  const locale = useLocale();
  const isGiftEnrollment = enrollment.__typename === "GiftEnrollment";

  const [
    withdrawAndRefundGiftEnrollment,
    { loading: giftEnrollmentMutationInProgress }
  ] = useMutation<
    WithdrawAndRefundGiftEnrollmentMutation,
    WithdrawAndRefundGiftEnrollmentMutationVariables
  >(withdrawAndRefundGiftEnrollmentMutation, {
    refetchQueries: [
      {
        query: EnrollmentListQuery,
        variables: getEnrollmentListQueryVariables(section.uid)
      }
    ]
  });

  const [withdrawAndRefundEnrollment, { loading: mutationInProgress }] =
    useMutation<
      WithdrawAndRefundEnrollmentMutation,
      WithdrawAndRefundEnrollmentMutationVariables
    >(withdrawAndRefundEnrollmentMutation, {
      refetchQueries: [
        {
          query: EnrollmentListQuery,
          variables: getEnrollmentListQueryVariables(section.uid)
        }
      ]
    });
  const refundNoteInputRef = useRef<HTMLTextAreaElement>(null);
  const refundReasonInputRef = useRef<HTMLSelectElement>(null);

  const [error, setError] = useState<string | null>(null);
  const [refundReason, setRefundReason] = useState("");
  const [refundNote, setRefundNote] = useState("");
  const parentRefundReasonLabels = useParentRefundReasonLabels(section);
  const trackRefund = useOrderRefunded();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!refundReason) {
      refundReasonInputRef.current?.focus();
      setError(t`Please choose a reason`);
      return false;
    }
    if (!refundNote && refundReason === EnrollmentPaymentRefund.REASONS.OTHER) {
      refundNoteInputRef.current?.focus();
      setError(t`Please add a note with more details`);
      return false;
    }
    try {
      const { data } = isGiftEnrollment
        ? await withdrawAndRefundGiftEnrollment({
            variables: {
              enrollmentUid: enrollment.uid,
              refundReason,
              refundNote
            }
          })
        : await withdrawAndRefundEnrollment({
            variables: {
              enrollmentUid: enrollment.uid,
              refundReason,
              refundNote
            }
          });

      if (!isGiftEnrollment) {
        const leader = activity.leader;
        const payments = enrollment.payments;
        const orderId = _.uniq(payments.map(el => el.order_slug_id));
        orderId.forEach(el => {
          // @ts-ignore TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
          trackRefund(el, enrollment.priceCents, payments[0].currency_code, {
            activity,
            leader
          });
        });
      }

      if (!data) {
        return false;
      }
      const classTitle = activity.titleTranslated;
      const userEmail = currentUser?.email;
      const learnerWithdrawnMessage = Enrollment.isFree(enrollment)
        ? studentName
          ? t("{{studentName}} has withdrawn from {{classTitle}}.", {
              studentName,
              classTitle
            })
          : t(
              "An unclaimed gift enrollment has withdrawn from {{classTitle}}.",
              { classTitle }
            )
        : studentName
        ? t(
            "{{studentName}} has withdrawn from {{classTitle}}. We will send a confirmation to {{userEmail}}.",
            { studentName, classTitle, userEmail }
          )
        : t(
            "An unclaimed gift enrollment has withdrawn from {{classTitle}}. We will send a confirmation to {{userEmail}}.",
            { classTitle, userEmail }
          );
      setTopNotice(learnerWithdrawnMessage);
    } catch (e) {
      setError(e);
    }
    return false;
  };

  const currencyEnrollment = useCurrencyEnrollmentEnabled();

  const currencyInfo = currencyEnrollment
    ? {
        currencyCode: enrollment.currency_code,
        exchangeRate:
          enrollment.payments && enrollment.payments.length > 0
            ? enrollment.payments[0].exchange_rate
            : 1.0
      }
    : {
        currencyCode: CurrencyCode.Usd,
        exchangeRate: 1
      };

  const totalUnrefundedCents = _.sumBy(
    enrollment.payments,
    enrollmentPayment => enrollmentPayment.unrefundedCents
  );
  const totalUnrefundedCredits = _.sumBy(
    enrollment.payments,
    enrollmentPayment => enrollmentPayment.unrefundedCredits ?? 0
  );
  const totalPaidByCredits = OSSubscriptions.priceCentsToCredits(
    _.sumBy(
      enrollment.payments,
      (enrollmentPayment: UpcomingEnrollmentPayment) =>
        enrollmentPayment.credit_transaction_uid &&
        enrollmentPayment.credit_account_type &&
        (enrollmentPayment.credit_account_type === "subscription" ||
          enrollmentPayment.credit_account_type === "esa_membership")
          ? enrollmentPayment.unrefundedCents
          : 0
    )
  );
  const paidByCredits = !!totalPaidByCredits;

  const usedPromoCode = enrollment.payments.some(ep => !!ep.coupon_code_uid);
  const withdrawAndRefundLabel =
    enrollment.refundableAmountCents === 0 && totalUnrefundedCents > 0
      ? t`Withdraw with no refund`
      : enrollment.refundableAmountCents < totalUnrefundedCents
      ? t`Withdraw with a partial refund`
      : enrollment.refundableAmountCents > 0 &&
        enrollment.refundableAmountCents === totalUnrefundedCents
      ? t`Withdraw with a full refund`
      : t`Withdraw`;

  const serviceFeePercentage = Number(
    enrollment.payments && enrollment.payments.length > 0
      ? enrollment.payments[0].service_fee_percentage
      : 0
  );
  const serviceFee = Math.ceil(
    (totalUnrefundedCents * serviceFeePercentage) / 100
  );

  // @ts-ignore TS(2345): Argument of type '{ currencyCode: CurrencyCode; ex... Remove this comment to see the full error message
  const amountPaid = renderCurrencyStringWithCurrencyConversion({
    amountUSDCents: totalUnrefundedCents + serviceFee,
    ...currencyInfo
  });

  // @ts-ignore TS(2345): Argument of type '{ currencyCode: CurrencyCode; ex... Remove this comment to see the full error message
  const refundableAmount = renderCurrencyStringWithCurrencyConversion({
    amountUSDCents:
      enrollment.refundableAmountCents > 0 &&
      enrollment.refundableAmountCents === totalUnrefundedCents &&
      serviceFeePercentage > 0
        ? enrollment.refundableAmountCents + serviceFee
        : enrollment.refundableAmountCents,
    ...currencyInfo
  });

  const refundInformationString = !!totalUnrefundedCredits ? (
    <>
      {t("You redeemed {{count}} credit({{refundableAmount}}).", {
        count: totalUnrefundedCredits,
        refundableAmount,
        defaultValue_plural:
          "You redeemed {{count}} credits({{refundableAmount}})."
      })}{" "}
      {enrollment.refundableAmountCents > 0
        ? t("{{count}} credit will be returned to your balance.", {
            count: totalUnrefundedCredits,
            defaultValue_plural:
              "{{count}} credits will be returned to your balance."
          })
        : t`This withdrawal will not return credits to your balance.`}
    </>
  ) : (
    <>
      {t("You have paid {{amountPaid}}", { amountPaid })}
      {paidByCredits &&
        t(" credit({{totalPaidByCredits}})", { totalPaidByCredits })}
      {". "}
      {enrollment.refundableAmountCents > 0 ? (
        <>
          {t("This will issue a refund of {{refundableAmount}}", {
            refundableAmount
          })}
          {paidByCredits &&
            t(" credit({{refundableCredits}})", {
              refundableCredits: OSSubscriptions.priceCentsToCredits(
                enrollment.refundableAmountCents
              )
            })}
          {". "}
        </>
      ) : (
        t`This withdrawal will not issue a refund.`
      )}
    </>
  );

  return (
    <React.Fragment>
      <Button
        ref={ref}
        variant="link"
        onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
          onClick?.(event);
          setOpen(true);
        }}
        {...buttonProps}
      >
        {t`Withdraw`}
      </Button>
      <PageModal open={open} onClose={() => setOpen(false)}>
        <ActivitySummary
          activity={activity}
          startTime={section.start_time}
          endTime={section.end_time}
          nextOngoingMeeting={section.nextOngoingMeeting}
        />
        <Typography variant="h3">
          {t("Withdraw {{studentName}} from this class", { studentName })}
        </Typography>
        <ErrorMessage
          value={error}
          showStatusPageLink={Env.IS_READ_ONLY_MODE}
        />
        <Box
          sx={{
            marginBottom: "1em"
          }}
        >
          <Trans t={t}>
            Please{" "}
            <Link
              to={addParamsToUrl(enrolledPath(section.uid), {
                openMB: "messageToLeader"
              })}
            >
              contact the teacher
            </Link>{" "}
            before you withdraw to address any concerns.
          </Trans>
        </Box>
        <form onSubmit={handleSubmit}>
          <label>
            {t`Why are you withdrawing?`}
            <Select
              name="refundReason"
              options={Object.entries(parentRefundReasonLabels).map(
                ([value, label]) => ({
                  value,
                  label
                })
              )}
              ref={refundReasonInputRef}
              value={refundReason}
              placeholder={t`Choose a reason...`}
              onChange={(value: string) => {
                setRefundReason(value);
                setError(null);
              }}
              sx={{
                width: "100%",
                boxSizing: "border-box",
                marginBottom: "0.5em"
              }}
            ></Select>
          </label>
          <label>
            <div>{t`Say more about your decision to withdraw:`}</div>
            <textarea
              value={refundNote}
              onChange={e => {
                setRefundNote(e.target.value);
                setError(null);
              }}
              onKeyDown={e => {
                e.stopPropagation();
              }}
              placeholder={t`Message to teacher and Outschool`}
              name="refundNoteInput"
              ref={refundNoteInputRef}
              rows={2}
              style={{
                width: "100%",
                boxSizing: "border-box",
                marginBottom: "0.5em",
                height: "auto"
              }}
            ></textarea>
          </label>
          {enrollment.canBePaused ? (
            <Alert
              severity="info"
              title={t`Did you want to pause your subscription instead?`}
              icon={<Icon icon={faInfoCircle} color="info" />}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center"
                }}
              >
                <Box>
                  <div>
                    {t`Pausing will temporarily unsubscribe you so you will not be charged.`}{" "}
                  </div>
                  <ExternalLink
                    url={SupportRoutes.supportOngoingClassesForLearnersUrl(
                      locale
                    )}
                  >
                    {t`Learn more`}
                  </ExternalLink>
                </Box>
                <TrackedButton
                  trackingName="Redirected to Pause Enrollment"
                  trackingUniqueId={section?.uid}
                  trackingSharedProperties={{
                    sectionUid: section?.uid,
                    currentUserUid: currentUser?.uid
                  }}
                  onClick={() => {
                    setOpen(false);
                    redirectToPause();
                  }}
                >{t`Pause Class`}</TrackedButton>
              </Box>
            </Alert>
          ) : (
            <></>
          )}
          <Box
            sx={{
              my: "0.5em"
            }}
          >
            {refundInformationString}{" "}
            {usedPromoCode &&
              t`Promotions used for this enrollment are non-refundable.`}
          </Box>
          {!!totalUnrefundedCredits &&
          enrollment.refundPolicy?.creditDescription ? (
            <Box
              sx={{
                marginTop: "1em",
                marginBottom: "2em"
              }}
            >
              {t("Class Cancellation Policy:")}{" "}
              {enrollment.refundPolicy?.creditDescription}
            </Box>
          ) : (
            <Box
              sx={{
                marginTop: "1em",
                marginBottom: "2em"
              }}
            >
              {t("{{refundPolicyName}} refund policy:", {
                refundPolicyName: enrollment.refundPolicy?.name
              })}{" "}
              {enrollment.refundPolicy?.description}
            </Box>
          )}
          <ModalActionButtons
            onCancel={() => setOpen(false)}
            primaryLabel={
              mutationInProgress || giftEnrollmentMutationInProgress
                ? t`Withdrawing...`
                : withdrawAndRefundLabel
            }
            primaryType="submit"
            disabled={mutationInProgress || giftEnrollmentMutationInProgress}
          />
        </form>
      </PageModal>
    </React.Fragment>
  );
}
